import { HEX_COLOR_REGEX } from "@redotech/util/color";

export function hexToRgb(
  hex: string,
): { r: number; g: number; b: number } | undefined {
  const match = HEX_COLOR_REGEX.exec(hex.trim());
  const captured = match?.[1];
  if (!captured) {
    return undefined;
  }
  const value =
    captured.length === 3
      ? captured
          .split("")
          .map((c) => c + c)
          .join("")
      : captured;
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

// Perceived luminance (Rec. 709), normalized to 0–1. Returns undefined when
// `color` is unparseable so callers can fall back to defaults rather than
// applying a wrong-direction override.
export function perceivedLuminance(color: string): number | undefined {
  const rgb = hexToRgb(color);
  if (!rgb) {
    return undefined;
  }
  return (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
}

// A "light" background is white, off-white, or very-light grey (luminance
// ≥ 0.85). Anything darker — pastels, mid-tones, true dark — is treated as
// non-light, which is the trigger for overrides like swapping success/error
// text tokens to the neutral primary text color so they don't clash with the
// surface.
const LIGHT_LUMINANCE_THRESHOLD = 0.85;

export function isLightColor(color: string): boolean | undefined {
  const luminance = perceivedLuminance(color);
  return luminance === undefined
    ? undefined
    : luminance >= LIGHT_LUMINANCE_THRESHOLD;
}

// A dark surface reads dividers and borders off the design-system
// fg-tertiary-600 token so they match the rest of the dark-mode chrome; a
// light surface gets a subtle translucent-black hairline. Returns undefined
// when `background` is unparseable so the caller falls back to defaults.
export function resolveDividerColor(
  background: string | undefined,
): string | undefined {
  if (background === undefined) {
    return undefined;
  }
  const luminance = perceivedLuminance(background);
  if (luminance === undefined) {
    return undefined;
  }
  return luminance < 0.5
    ? "var(--redo-colors-foreground-fg-tertiary)"
    : "rgba(0, 0, 0, 0.15)";
}

// Mix `color` toward black (light colors) or white (dark colors) by `ratio`
// so the result reads as a "deeper" version of the original. Used for hover
// states where a link/button should darken slightly when the cursor is over
// it. Returns undefined for unparseable inputs so callers can skip the rule.
export function resolveHoverColor(
  color: string | undefined,
  ratio = 0.15,
): string | undefined {
  if (color === undefined) {
    return undefined;
  }
  const rgb = hexToRgb(color);
  if (!rgb) {
    return undefined;
  }
  const luminance = (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
  const target = luminance < 0.5 ? 255 : 0;
  const mix = (channel: number) =>
    Math.round(channel * (1 - ratio) + target * ratio);
  const r = mix(rgb.r);
  const g = mix(rgb.g);
  const b = mix(rgb.b);
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}
